(() => {
  'use strict';

  const FIELD_SELECTOR = 'input, select, textarea';
  const WRAPPER_SELECTOR = '.builder-field, .field, .form-field, .input-group, .finish-selector, .configurator-step';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isField = element =>
    element instanceof HTMLInputElement ||
    element instanceof HTMLSelectElement ||
    element instanceof HTMLTextAreaElement;

  const validationWrapper = field => {
    if (field.type === 'radio' || field.type === 'checkbox') {
      return field.closest('.finish-options, .radio-group, .checkbox-group, .field, .builder-field');
    }
    return field.closest(WRAPPER_SELECTOR);
  };

  const setInvalidState = (field, invalid) => {
    if (!isField(field)) return;

    field.classList.toggle('ryvik-invalid-field', invalid);
    field.setAttribute('aria-invalid', invalid ? 'true' : 'false');

    const wrapper = validationWrapper(field);
    const needsGroupOutline = field.type === 'radio' || field.type === 'checkbox';
    if (wrapper) wrapper.classList.toggle('ryvik-validation-error', invalid && needsGroupOutline);
  };

  const validateField = field => {
    if (!isField(field) || field.disabled || !field.willValidate) return true;
    const valid = field.checkValidity();
    setInvalidState(field, !valid);
    return valid;
  };

  const requiredFields = form => Array.from(form.querySelectorAll(FIELD_SELECTOR))
    .filter(field => field.willValidate && !field.disabled);

  const prepareForm = form => {
    if (!(form instanceof HTMLFormElement)) return;
    form.noValidate = true;
    form.setAttribute('novalidate', 'novalidate');
  };

  const prepareAllForms = root => {
    if (root instanceof HTMLFormElement) prepareForm(root);
    if (root.querySelectorAll) root.querySelectorAll('form').forEach(prepareForm);
  };

  prepareAllForms(document);

  /* Also cover forms injected later by Shopify sections, apps, or cart UI. */
  const observer = new MutationObserver(records => {
    records.forEach(record => record.addedNodes.forEach(node => {
      if (node instanceof Element) prepareAllForms(node);
    }));
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  /* Suppress every native validation bubble, including the browser's
     “Please fill out this field” message. */
  document.addEventListener('invalid', event => {
    event.preventDefault();
    const field = event.target;
    if (isField(field)) setInvalidState(field, true);
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    prepareForm(form);
    let firstInvalid = null;

    requiredFields(form).forEach(field => {
      const valid = validateField(field);
      if (!valid && !firstInvalid) firstInvalid = field;
    });

    if (!firstInvalid) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const target = (firstInvalid.type === 'radio' || firstInvalid.type === 'checkbox')
      ? (validationWrapper(firstInvalid) || firstInvalid)
      : firstInvalid;
    target.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'center'
    });

    window.setTimeout(() => {
      try { firstInvalid.focus({ preventScroll: true }); }
      catch (_) { firstInvalid.focus(); }
    }, reduceMotion ? 0 : 260);
  }, true);

  const clearOrRefresh = event => {
    const field = event.target;
    if (!isField(field)) return;

    /* Once a field has been marked, update the red outline live while the
       customer corrects it. Untouched fields remain visually neutral. */
    if (field.getAttribute('aria-invalid') === 'true' || field.classList.contains('ryvik-invalid-field')) {
      validateField(field);
    }
  };

  document.addEventListener('input', clearOrRefresh, true);
  document.addEventListener('change', clearOrRefresh, true);
  document.addEventListener('blur', clearOrRefresh, true);
})();
